import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { AnalyticsBase } from '@core/analytics-legacy';
import { ChurnFlowAnalyticsI, ChurnFlowAlternativeSolution } from '@core/churn-flow-types';
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import { AppKind, injectTellentAppKind } from '@core/tellent-types';

type ChurnContext = Pick<ChurnFlowAnalyticsI, 'app' | 'orgGuid' | 'orgName'>;

@Injectable()
export class ChurnFlowAnalyticsBaseService extends AnalyticsBase {
  private readonly app: AppKind = injectTellentAppKind();
  private readonly store = inject(Store);
  private readonly currentMembership = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );
  private readonly orgGuid = this.currentMembership()?.org?.guid;
  private readonly orgName = this.currentMembership()?.org?.name;

  public readonly namespace: string = 'Churn';
  public readonly requireViewContext: boolean = false;

  private context: ChurnContext = {
    app: this.app,
    orgGuid: this.orgGuid,
    orgName: this.orgName,
  };

  public trackCancelSubscriptionStarted(): void {
    this.track('CancelSubscriptionStarted', { ...this.context });
  }

  public trackStayWithTellent(data: ChurnFlowAnalyticsI): void {
    this.track('StayWithTellent', { ...this.context, ...data });
  }

  public trackStepOneContactSupport(): void {
    this.track('Step1_ContactSupport', { app: this.app });
  }

  public trackStepOneModifyMyPlan(): void {
    this.track('Step1_ModifyPlan', { app: this.app });
  }

  public trackStepOneContinue(): void {
    this.track('Step1_ContinueWithCancellation', { app: this.app });
  }

  public trackStepTwoContinue(data: ChurnFlowAnalyticsI): void {
    this.track('Step2_ReasonSelected', { ...this.context, ...data });
  }

  public trackAlternativeSelected(alternative: ChurnFlowAlternativeSolution): void {
    this.track('Step3_AlternativeSelected', { ...this.context, alternative });
  }

  public trackStepThreeContinue(data: ChurnFlowAnalyticsI): void {
    this.track('Step3_ContinueWithCancellation', { ...this.context, ...data });
  }

  public trackStepFourContinue(data: ChurnFlowAnalyticsI): void {
    this.track('Step4_CompleteCancellation', { ...this.context, ...data });
  }

  public trackSuccessfulCancellation(): void {
    this.track('Step4_SuccessfulCancellation', { ...this.context });
  }

  public trackFarewellStepContinue(data: ChurnFlowAnalyticsI): void {
    this.track('SubscriptionCancelled', { ...this.context, ...data });
  }
}
